import { connect } from "react-redux";
import {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
  updateGlobalSearch,
} from "../../actions";
import { Progress, Table, Row, Col } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Animations from "../../animations/Animations";
import MainLayout from "../../components/layouts/main";
import Constants from "../../data/constants";
import { THEME_PALLETE } from "../../Theme";
import {
  LayoutDashboard,
  MonitorPlay,
  FolderOpen,
  HardDrive,
  Wifi,
  WifiOff,
  Film,
  Clock,
  FileImage,
} from "lucide-react";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const MEDIA_TYPE_COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#f43f5e",
  "#0ea5e9",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
];
const PLATFORM_COLORS = ["#0ea5e9", "#10b981", "#94a3b8"];

// ── Stat card ──────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, gradient, sub }) => (
  <motion.div
    variants={Animations.LIST_ITEM}
    style={{
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #f1f5f9",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      padding: "20px 22px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      height: "100%",
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 14,
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 30,
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          marginTop: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
          {sub}
        </div>
      )}
    </div>
  </motion.div>
);

// ── Section card (for charts / table) ─────────────────────────────────────
const SectionCard = ({ title, icon, children, style = {} }) => (
  <motion.div
    variants={Animations.LIST_ITEM}
    style={{
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #f1f5f9",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      overflow: "hidden",
      height: "100%",
      ...style,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 20px",
        borderBottom: "1px solid #f1f5f9",
        background: "#fafbfc",
      }}
    >
      {icon && (
        <span style={{ display: "flex", color: "#6366f1" }}>{icon}</span>
      )}
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#0f172a",
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </span>
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </motion.div>
);

function DashboardPage(props) {
  const { lang } = useProLanguage();
  const t = PRO_TRANSLATIONS[lang].dashboard;

  const [dashboardData, setDashboardData] = useState({
    ScreenOnline: 0,
    ScreenOffline: 0,
    ScreenTotal: 0,
    OS: { PC: 0, Android: 0, "N/A": 0 },
    Media: { Image: 0, Video: 0, VideoURL: 0, Web: 0, StreamingURL: 0 },
    PublishedScreens: [],
    TotalStorage: "0 MB",
    MediaTotal: 0,
    MediaUnused: 0,
    MediaExpired: 0,
  });

  useEffect(() => {
    // Simulated data
    // setDashboardData({
    //     totalScreens: 130,
    //     activeScreens: 120,
    //     mediaFiles: { images: 500, videos: 200 },
    //     storageUsage: 75,
    //     networkStatus: 92,
    //     platformDistribution: { Windows: 60, Android: 40 },
    //     uptime: { "24h": 98, "7d": 96, "30d": 95 },
    //     downtimeAlerts: [
    //         { screen: "Screen 12", duration: "3h 20m" },
    //         { screen: "Screen 25", duration: "1h 10m" }
    //     ],
    //     softwareVersions: { "v1.2": 80, "v1.3": 50 },
    //     mostPlayedMedia: ["Ad1.mp4", "Promo2.jpg", "Event3.mp4"],
    //     contentDistribution: { video: 60, image: 30, other: 10 },
    //     recentPublished: [
    //         { screen: "Screen 5", branch: "Branch A", date: "2024-07-08 16:30" },
    //         { screen: "Screen 12", branch: "Branch A", date: "2024-07-08 15:50" },
    //         { screen: "Screen 5", branch: "Branch B", date: "2024-07-08 14:37" },
    //         { screen: "Screen 2", branch: "Branch B", date: "2024-07-08 12:10" },
    //         { screen: "Screen 8", branch: "Branch A", date: "2024-07-08 12:01" },
    //         { screen: "Screen 1", branch: "Branch C", date: "2024-07-08 11:08" },
    //         { screen: "Screen 8", branch: "Branch A", date: "2024-07-08 11:01" },
    //         { screen: "Screen 7", branch: "Branch C", date: "2024-06-08 10:52" },
    //         { screen: "Screen 7", branch: "Branch B", date: "2024-06-08 10:50" },
    //         { screen: "Screen 5", branch: "Branch C", date: "2024-07-08 16:30" },
    //         { screen: "Screen 12", branch: "Branch A", date: "2024-07-08 15:50" },
    //         { screen: "Screen 5", branch: "Branch D", date: "2024-07-08 14:37" },
    //         { screen: "Screen 2", branch: "Branch A", date: "2024-07-08 12:10" },
    //         { screen: "Screen 8", branch: "Branch B", date: "2024-07-08 12:01" },
    //         { screen: "Screen 1", branch: "Branch A", date: "2024-07-08 11:08" },
    //         { screen: "Screen 8", branch: "Branch A", date: "2024-07-08 11:01" },
    //         { screen: "Screen 7", branch: "Branch A", date: "2024-06-08 10:52" },
    //         { screen: "Screen 7", branch: "Branch A", date: "2024-06-08 10:50" },
    //     ],
    //     unpublishedScreens: { unpublished: 10, published: 120 }
    // });

    getDashboardData();
  }, []);

  const getDashboardData = () => {
    const { usertoken, requestServerGet, selectedBrandData } = props;

    if (!selectedBrandData) return;

    requestServerGet(
      "get_dashboard",
      { brandid: selectedBrandData.BrandID },
      null,
      (response) => {
        console.log("Dashboard full:", JSON.stringify(response.data));
        // const options = response.data.map(data => {
        //     return { value: data.CampaignID, label: data.CampaignName }
        // })

        setDashboardData({ ...dashboardData, ...response.data });
        // this.setState({ campaigntDatas: options, campaignOptions: options })
      },
      usertoken,
    );
  };

  const mediaData = Object.keys(dashboardData.Media)
    .map((key, i) => ({ name: key, value: dashboardData.Media[key] }))
    .filter((e) => e.value > 0);

  const osData = Object.keys(dashboardData.OS)
    .map((key, i) => ({ name: key, value: dashboardData.OS[key] }))
    .filter((e) => e.value > 0);

  const total = dashboardData.ScreenTotal || 1;
  const onlinePct = Math.round((dashboardData.ScreenOnline / total) * 100);

  const unusedPct = dashboardData.MediaTotal
    ? Math.round((dashboardData.MediaUnused / dashboardData.MediaTotal) * 100)
    : 0;
  const expiredPct = dashboardData.MediaTotal
    ? Math.round((dashboardData.MediaExpired / dashboardData.MediaTotal) * 100)
    : 0;

  return (
    <MainLayout
      id={Constants.DASHBOARD_PAGE}
      quietTitleBar={true}
      style={{ padding: 20 }}
      showTopBarBrandLogo={true}
      showSearchBar={true}
      renderBreadCrumb={() => [
        <span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", padding: "4px 8px", lineHeight: "22px" }}>
          {t.title || "Dashboard"}
        </span>,
      ]}
    >
      <motion.div
        variants={Animations.LIST_CONTAINER}
        initial="hidden"
        animate="show"
        style={{ padding: 0 }}
      >
        {/* ── Page header ───────────────────────────────────────── */}
        <motion.div
          variants={Animations.LIST_ITEM}
          style={{ marginBottom: 20 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              borderRadius: 16,
              padding: "20px 24px",
              border: "1px solid #e8ecf3",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  flexShrink: 0,
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LayoutDashboard size={26} color="#fff" />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1.2,
                  }}
                >
                  {t.title}
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>
                  {props.selectedBrandData
                    ? props.selectedBrandData.BrandName
                    : t.overview}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "4px 12px",
                borderRadius: 20,
                background: "rgba(245,158,11,0.09)",
                border: "1px solid rgba(245,158,11,0.22)",
              }}
            >
              <span
                style={{
                  color: "#d97706",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                }}
              >
                {t.proEdition}
              </span>
            </div>
          </div>
        </motion.div>

        <Row gutter={[16, 16]}>
          {/* ── KPI stat cards ────────────────────────────────────── */}
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label={t.onlineScreens}
              value={dashboardData.ScreenOnline.toLocaleString()}
              icon={<Wifi size={22} color="#fff" />}
              gradient="linear-gradient(135deg,#10b981,#059669)"
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label={t.offlineScreens}
              value={dashboardData.ScreenOffline.toLocaleString()}
              icon={<WifiOff size={22} color="#fff" />}
              gradient="linear-gradient(135deg,#f43f5e,#e11d48)"
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label={t.totalScreens}
              value={dashboardData.ScreenTotal.toLocaleString()}
              icon={<MonitorPlay size={22} color="#fff" />}
              gradient="linear-gradient(135deg,#6366f1,#4f46e5)"
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label={t.totalMediaFiles}
              value={dashboardData.MediaTotal.toLocaleString()}
              icon={<FolderOpen size={22} color="#fff" />}
              gradient="linear-gradient(135deg,#0ea5e9,#0284c7)"
            />
          </Col>

          {/* ── Screen health + Media stats ────────────────────────── */}
          <Col xs={24} md={12}>
            <SectionCard
              title={t.screenConnectivity}
              icon={<MonitorPlay size={15} />}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 13, color: "#64748b" }}>
                  {t.onlineVsOffline}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color:
                      onlinePct >= 80
                        ? "#10b981"
                        : onlinePct >= 50
                          ? "#f59e0b"
                          : "#f43f5e",
                  }}
                >
                  {onlinePct}% Online
                </span>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 5,
                  background: "#fee2e2",
                  overflow: "hidden",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${onlinePct}%`,
                    borderRadius: 5,
                    background: "linear-gradient(90deg,#10b981,#059669)",
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#10b981",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {t.online}{" "}
                    <b style={{ color: "#0f172a" }}>
                      {dashboardData.ScreenOnline}
                    </b>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#f43f5e",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {t.offline}{" "}
                    <b style={{ color: "#0f172a" }}>
                      {dashboardData.ScreenOffline}
                    </b>
                  </span>
                </div>
              </div>
            </SectionCard>
          </Col>
          <Col xs={24} md={12}>
            <SectionCard
              title={t.mediaStorageHealth}
              icon={<HardDrive size={15} />}
            >
              {[
                {
                  label: t.totalStorage,
                  value: dashboardData.TotalStorage,
                  pct: null,
                  color: "#6366f1",
                },
                {
                  label: t.unusedFiles,
                  value: `${dashboardData.MediaUnused} files`,
                  pct: unusedPct,
                  color: "#f59e0b",
                },
                {
                  label: t.expiredFiles,
                  value: `${dashboardData.MediaExpired} files`,
                  pct: expiredPct,
                  color: "#f43f5e",
                },
              ].map(({ label, value, pct, color }) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 5,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: "#64748b",
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                  {pct !== null && (
                    <div
                      style={{
                        height: 6,
                        borderRadius: 3,
                        background: "#f1f5f9",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${pct}%`,
                          borderRadius: 3,
                          background: color,
                          transition: "width 0.6s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </SectionCard>
          </Col>

          {/* ── Charts ────────────────────────────────────────────── */}
          <Col xs={24} md={12}>
            <SectionCard
              title={t.mediaTypesDistribution}
              icon={<Film size={15} />}
            >
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      data={mediaData}
                      paddingAngle={3}
                    >
                      {mediaData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={MEDIA_TYPE_COLORS[i % MEDIA_TYPE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #f1f5f9",
                        fontSize: 13,
                      }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={10}
                      wrapperStyle={{ fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </Col>
          <Col xs={24} md={12}>
            <SectionCard
              title={t.platformDistribution}
              icon={<MonitorPlay size={15} />}
            >
              <div style={{ width: "100%", height: 280 }}>
                {osData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={100}
                        data={osData}
                        paddingAngle={3}
                      >
                        {osData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={PLATFORM_COLORS[i % PLATFORM_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #f1f5f9",
                          fontSize: 13,
                        }}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={10}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <MonitorPlay size={36} color="#e2e8f0" />
                    <span style={{ fontSize: 13, color: "#94a3b8" }}>
                      No platform data available
                    </span>
                  </div>
                )}
              </div>
            </SectionCard>
          </Col>

          {/* ── Recently Published ────────────────────────────────── */}
          <Col xs={24}>
            <SectionCard title={t.recentlyPublished} icon={<Clock size={15} />}>
              {(() => {
                const rows = (dashboardData?.PublishedScreens ?? []).filter(
                  (r) => r?.ScreenName?.trim(),
                );
                if (rows.length === 0) {
                  return (
                    <div
                      style={{
                        padding: "32px 0",
                        color: "#94a3b8",
                        fontSize: 13,
                        textAlign: "center",
                      }}
                    >
                      No publish history yet
                    </div>
                  );
                }
                const cols = [
                  {
                    label: t.screen,
                    key: "ScreenName",
                    render: (v) => (
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>
                        {v}
                      </span>
                    ),
                  },
                  {
                    label: t.branch,
                    key: "BranchName",
                    render: (v) => (
                      <span style={{ color: "#64748b" }}>{v}</span>
                    ),
                  },
                  { label: t.by, key: "UserName", render: (v) => v },
                  {
                    label: t.date,
                    key: "PublishDate",
                    render: (v) => (
                      <span style={{ color: "#94a3b8", fontSize: 12 }}>
                        {v}
                      </span>
                    ),
                  },
                ];
                const thStyle = {
                  padding: "8px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#94a3b8",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  borderBottom: "1px solid #f1f5f9",
                  background: "#fafbfc",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                };
                const tdStyle = {
                  padding: "10px 12px",
                  fontSize: 13,
                  borderBottom: "1px solid #f8fafc",
                  verticalAlign: "middle",
                };
                return (
                  <div
                    style={{
                      overflowX: "auto",
                      overflowY: "auto",
                      maxHeight: 300,
                      margin: "-16px -20px",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          {cols.map((c) => (
                            <th key={c.key} style={thStyle}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr
                            key={i}
                            style={{
                              background: i % 2 === 0 ? "#fff" : "#fafbfc",
                            }}
                          >
                            {cols.map((c) => (
                              <td key={c.key} style={tdStyle}>
                                {c.render(row[c.key])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </SectionCard>
          </Col>
        </Row>
      </motion.div>
    </MainLayout>
  );
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    globalSearch: state.globalSearch,
    selectedBrandData: state.selectedBrandData,
  };
};

export default connect(mapStateToProps, {
  updateGlobalSearch,
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
})(DashboardPage);
